import React, { useState, useEffect } from "react";
import Table from "../../components/Table/Table";
import TopNavbar from "../../components/TopNavbar/TopNavbar";
import ApiManager from "../../ApiManager/ApiManager";
import NewFuelTransaction from "./NewFuelTransction";
import Modal from "../../components/Modal/Modal";
import ViewEntity from "../../components/ViewEntity/ViewEntity";
import FilterBar from "../../components/FilterBar/FilterBar";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import {useAuth} from "../../components/AuthProvider";
import { ToastContainer, toast } from "react-toastify";
import {
  hasPermission,
  ShowButton,
} from "../../components/RoleBasedComponents";
import PERMISSIONS from "../../permissions";
import { prepareFileForModal } from "../../utils/fileUtils";
import FileViewer from "../../components/FileViewer/FileViewer";
import EditFuelTransaction from "./EditFuelTransaction";

function Fuels() {
  const { userPermissions, vendorDetails } = useAuth();
  const {
    loadingStates,
    loadingMessages,
    loadingProgress,
    withLoading,
    isLoading
  } = useComponentLoading({
    fetchData: false,
    fileView: false,
    approve: false,
    reject: false,
    exportData: false
  });

  const [data, setData] = useState([]);
  const [rowId, setRowId] = useState(null);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isDetails, setDetails] = useState(false);
  const [isEdit, setEdit] = useState(false);
  const [isExportModal, setIsExportModal] = useState(false);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [fuelReceiptStatus, setFuelReceiptStatus] = useState("");
  const [showFile, setShowFile] = useState(false);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [emails, setEmails] = useState(vendorDetails?.email ? [vendorDetails.email] : []);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [approveMessage, setApproveMessage] = useState("");
  const [currentRowId, setCurrentRowId] = useState(null);

  console.log("Filter Component Rendered");

  const handleFileClick = async (fileName) => {
    try {
      // Set loading state and open modal immediately
      setModalOpen(true);
      setShowFile(true);
      setEdit(false);
      setIsExportModal(false);
      setDetails(false);
      setFileUrl(null);
      setFileType(null);

      await withLoading('fileView', async (updateProgress) => {
        // Use the utility function to prepare the file for viewing in the modal
        await prepareFileForModal(fileName, setFileUrl, setFileType, updateProgress);

        updateProgress(100);
      }, 'Loading file...', 30000);
    } catch (error) {
      console.error("Error fetching file:", error);
      // Close modal on error
      setModalOpen(false);
      setShowFile(false);
      setIsExportModal(false);
      toast.error("Failed to load file. Please try again.");
    }
  };

  const toggleModal = () => {
    setModalOpen(!isModalOpen);
  };

  const handleEdit = () => {
    console.log("Edit clicked for row:");
    setEdit(true);
    setModalOpen(true);
    setDetails(false);
    setShowFile(false);
    setIsExportModal(false);
  };

  const handleDetails = () => {
    console.log("Details clicked for row:");
    setDetails(true);
    setModalOpen(true);
    setEdit(false);
    setShowFile(false);
    setIsExportModal(false);
  };

  const handleExportModal = () => {
    setIsExportModal(true);
    setModalOpen(true);
    setEdit(false);
    setDetails(false);
    setShowFile(false);

  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEdit(false);
    setDetails(false);
    setShowFile(false);
    setIsExportModal(false);
  };

  const handleRowClick = (row) => {
    setRowId(row);
  };

  const handleApproved = async () => {
    try {
      await withLoading('approve', async (updateProgress) => {
        updateProgress(30);
        const response = await ApiManager.markFuelTransactionApproved(
          new URLSearchParams({ id: rowId, check: 1 })
        );
        const result = await response.json();
        updateProgress(70);
        if (response.ok && result.message === "success") {
          // Proceed with actual approval
          await ApiManager.markFuelTransactionApproved(
            new URLSearchParams({ id: rowId, check: 0 })
          );
          setFuelReceiptStatus("approved");
          fetchData();
          updateProgress(100);
        } else {
          // Show modal with message and options
          setApproveMessage(result.message || "Approval requires confirmation.");
          setShowApproveConfirm(true);
        }
        setCurrentRowId(rowId);
      }, 'Approving transaction...', 30000);
    } catch (error) {
      console.error("Error approving transaction:", error);
    }
  };

  const handleRejected = async () => {
    try {
      await withLoading('reject', async (updateProgress) => {
        updateProgress(30);

        const response = await ApiManager.markFuelTransactionRejected(
          new URLSearchParams({ id: rowId })
        );

        updateProgress(70);

        if (!response.ok) throw new Error("Network response was not ok");
        setFuelReceiptStatus("rejected");
        fetchData();

        updateProgress(100);
      }, 'Rejecting transaction...', 30000);
    } catch (error) {
      console.error("Error rejecting transaction:", error);
    }
  };

  const buttons = [{ label: "Details", onClick: () => handleDetails() }];

  if (
    hasPermission(
      userPermissions,
      PERMISSIONS.VENDOR_FUEL_TRANSACTION_APPROVED_UPDATE
    )
  ) {
    buttons.push({ label: "Approve", onClick: () => handleApproved() });
  }

  if (
    hasPermission(
      userPermissions,
      PERMISSIONS.VENDOR_FUEL_TRANSACTION_REJECTED_UPDATE
    )
  ) {
    buttons.push({ label: "Reject", onClick: () => handleRejected() });
  }

  if (
    hasPermission(userPermissions, PERMISSIONS.VENDOR_FUEL_TRANSACTION_UPDATE)
  ) {
    buttons.push({ label: "Edit", onClick: () => handleEdit() });
  }

  const fetchData = async () => {
    try {
      await withLoading('fetchData', async (updateProgress) => {
        // Simulate progress updates during data fetching
        updateProgress(10);

        const params = new URLSearchParams({
          from: filters.startTime ? new Date(filters.startTime).getTime() / 1000 : 0,
          to: filters.endTime ? new Date(filters.endTime).getTime() / 1000 : 1993456000,
          page_size: 20,
          page: currentPage,
          driverId: filters.driverId || "",
          carId: filters.carId || "",
          status: filters.status || "",
        });

        updateProgress(30);

        const response = await ApiManager.getAllFuelTransactions(params);
        if (!response.ok) throw new Error("Failed to fetch fuel transactions");

        updateProgress(60);

        const json = await response.json();

        updateProgress(90);

        if (JSON.stringify(json) !== JSON.stringify(data)) {
          setData(json || []);
          setHasMorePages(json.length == 20);
        }

        updateProgress(100);
      }, 'Loading transactions...', 30000);
    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
      setHasMorePages(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters, currentPage, fuelReceiptStatus]);

  const columns = [
    { field: "driver_name", headerName: "Driver" },
    { field: "car_number", headerName: "Car Number" },
    { field: "amount", headerName: "Amount" },
    { field: "fuel_amount", headerName: "Fuel Quantity (L)" },
    { field: "fuel_type", headerName: "Fuel Type" },
    { field: "is_paid_by_driver", headerName: "Driver Paid" },
    { field: "description", headerName: "Serial Number" },
    { field: "km_reading", headerName: "Km Reading" },
    { field: "status", headerName: "Status" },
    {
      field: "file_path_fuel",
      headerName: "Fuel Receipt",
      renderCell: (params) => {
        if (!params.row.file_path_fuel) return "N/A";

        return (
          <ShowButton permission={PERMISSIONS.VENDOR_FILE_READ}>
            <button
              onClick={() => handleFileClick(params.row.file_path_fuel)}
              className="custom-button-green"
            >
              View Receipt
            </button>
          </ShowButton>
        );
      },
    },

    {
      field: "file_path_km_reading",
      headerName: "km Reading Receipt",
      renderCell: (params) => {
        if (!params.row.file_path_km_reading) return "N/A";

        return (
          <ShowButton permission={PERMISSIONS.VENDOR_FILE_READ}>
            <button
              onClick={() => handleFileClick(params.row.file_path_km_reading)}
              className="custom-button-green"
            >
              View Receipt
            </button>
          </ShowButton>
        );
      },
    },
  ];
  const view_columns = [
    { field: "driver_name", headerName: "Driver" },
    { field: "car_number", headerName: "Car Number" },
    { field: "amount", headerName: "Amount" },
    { field: "fuel_type", headerName: "Fuel Type" },
    {
      field: "fuel_amount",
      headerName: "Fuel Quantity (litres)",
      type: "number",
    },
    { field: "is_paid_by_driver", headerName: "Driver Paid" },
    { field: "description", headerName: "Description" },
    { field: "km_reading", headerName: "Km Reading" },
  ];

  const rows = (data || []).map((item) => ({
    id: item.id,
    driver_name: item.driver_name,
    car_number: item.car_number,
    amount: item.amount,
    fuel_amount: item.fuel_amount,
    fuel_type: item.fuel_type,
    is_paid_by_driver: item.is_paid_by_driver,
    file_path_fuel: item.file_path_fuel,
    file_path_km_reading: item.file_path_km_reading,
    km_reading: item.km_reading,
    description: item.description,
    status: item.status == "created" ? "Pending" : item.status,
  }));

  const filterOptions = [
    { value: "driver", label: "Driver" },
    { value: "car", label: "Car" },
    { value: 'time', label: 'Time Range' },
    { value: 'status', label: 'Status' }
  ];

  const apiFunctions = {
    driver: ApiManager.getAllDrivers,
    car: ApiManager.getAllCars,
  };

  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
  };

  const handleFieldUpdate = async ({ rowId, field, newValue }) => {
    if(newValue === "approved") {
      try {
        await withLoading('approve', async (updateProgress) => {
          updateProgress(30);
          const response = await ApiManager.markFuelTransactionApproved(
            new URLSearchParams({ id: rowId, check: 1 })
          );
          const result = await response.json();
          updateProgress(70);
          if (response.ok && result.message === "success") {
            // Proceed with actual approval
            await ApiManager.markFuelTransactionApproved(
              new URLSearchParams({ id: rowId, check: 0 })
            );
            setData((prev) =>
              prev.map((item) => item.id === rowId ? { ...item, [field]: newValue } : item)
            );
            updateProgress(100);
          } else {
            // Show modal with message and options
            setApproveMessage(result.message || "Approval requires confirmation.");
            setShowApproveConfirm(true);
          }
          setCurrentRowId(rowId);
        }, 'Approving transaction...', 30000);
      } catch (error) {
        console.error("Error approving transaction:", error);
      }
    }
    else if(newValue === "rejected"){
      try {
        await withLoading('reject', async (updateProgress) => {
          updateProgress(30);

          const response = await ApiManager.markFuelTransactionRejected(
            new URLSearchParams({ id: rowId })
          );

          updateProgress(70);

          setData((prev) =>
            prev.map((item) => item.id === rowId ? { ...item, [field]: newValue } : item)
          );

          updateProgress(100);
        }, 'Rejecting transaction...', 30000);
        // alert("Rejected");
      } catch (error) {
        console.log("error", error);

      }
    }
  };

  const handleEmailChange = (e) => {
    setEmails(e.target.value.split(',').map(email => email.trim()));
  };

  const handleExportData = async () => {
    try {
      await withLoading('exportData', async (updateProgress) => {

        const params = new URLSearchParams({
          from: filters.startTime ? new Date(filters.startTime).getTime() / 1000 : 0,
          to: filters.endTime ? new Date(filters.endTime).getTime() / 1000 : 1993456000,
          driverId: filters.driverId || "",
          carId: filters.carId || "",
          status: filters.status || "",
        });
        updateProgress(10);

        const response = await ApiManager.exportFuelTransactions(params, {emails: emails});

        updateProgress(100);
        toast.success("Data exported successfully, you will receive an email shortly", {style: {fontFamily: 'var(--font-family)'}});
      }, 'Exporting data...', 30000);
      setIsExportModal(false);
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("Error exporting data");
    }
  };

  if(loadingStates.approve || loadingStates.reject){
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: '100%'
      }}>
        <LoadingSpinner
          message={
            loadingStates.approve ? loadingMessages.approve :
            loadingStates.reject ? loadingMessages.reject : ""
          }
          showProgress={true}
          progress={
            loadingStates.approve ? loadingProgress.approve :
            loadingStates.reject ? loadingProgress.reject : 0
          }
        />
      </div>
    )
  }
  return (
    <div className="fuel-container" style={{display: "flex", flexDirection: "column", gap: "10px"}}>
      <ToastContainer style={{ fontSize: 'var(--font-size-2xs)'}} />
      {/* Loading Spinner - Only show for non-table operations */}
      {/* {(loadingStates.approve || loadingStates.reject) && (
        <LoadingSpinner
          overlay={true}
          message={
            loadingStates.approve ? loadingMessages.approve :
            loadingStates.reject ? loadingMessages.reject : ""
          }
          progress={
            loadingStates.approve ? loadingProgress.approve :
            loadingStates.reject ? loadingProgress.reject : 0
          }
          showProgress={true}
        />
      )} */}

      {/* Debug info */}
      {/* {process.env.NODE_ENV === 'development' && (
        <div style={{ position: 'fixed', bottom: 10, right: 10, background: 'rgba(0,0,0,0.7)', color: 'white', padding: 10, zIndex: 9999 }}>
          <div>Loading States: {JSON.stringify(loadingStates)}</div>
          <div>Loading Progress: {JSON.stringify(loadingProgress)}</div>
        </div>
      )} */}

      <TopNavbar
        title="Fuels"
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          paddingTop: "20px",
          height: "5vh",
        }}
      >
        <FilterBar
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          apiFunctions={apiFunctions}
        />
        <div style={{ display: 'flex', gap: '10px' }}>
        <ShowButton permission={PERMISSIONS.VENDOR_FUEL_TRANSACTION_CREATE}>
          <NewFuelTransaction onSuccess={fetchData} />
        </ShowButton>

        <ShowButton permission={PERMISSIONS.VENDOR_FUEL_TRANSACTION_READ}>
          <button className="custom-button" onClick={handleExportModal}>
            Export Data
          </button>
        </ShowButton>
        </div>
      </div>

      <Table
        table_columns={columns}
        view_columns={columns}
        rows={rows}
        setRowsId={setRowId}
        buttons={buttons}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        hasMorePages={hasMorePages}
        onFieldUpdate={handleFieldUpdate}
        isLoading={loadingStates.fetchData}
        loadingMessage={loadingMessages.fetchData}
        loadingProgress={loadingProgress.fetchData}
      />
      {isModalOpen && isDetails && (
        <div className="details-container">
          <Modal isOpen={isModalOpen} onClose={handleModalClose}>
            <ViewEntity
              id={rowId}
              fetchFunction={ApiManager.getFuelTransaction}
              columns={view_columns}
              entity="id"
              loadingKey="viewDetails"
              loadingMessage="Loading transaction details..."
            />
          </Modal>
        </div>
      )}
      {isModalOpen && isEdit && (
        <div className="edit-container">
          <Modal isOpen={isModalOpen} onClose={handleModalClose} title="Edit Fuel Transaction">
            <EditFuelTransaction rowId={rowId} onSuccess={fetchData} toggleFunction={toggleModal}/>
          </Modal>
        </div>
      )}
      {
        isModalOpen && isExportModal && (
          <Modal isOpen={isModalOpen} onClose={handleModalClose} title="Export Data">
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              <div>
                {emails.map((email, index) => (
                  <div key={index} style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
                    <label style={{ marginRight: "10px", minWidth: "60px" }}>
                      Email {index + 1}:
                    </label>
                    <input
                      type="text"
                      value={email}
                      onChange={(e) => {
                        const newEmails = [...emails];
                        newEmails[index] = e.target.value;
                        setEmails(newEmails);
                      }}
                      style={{ marginRight: "10px", width: "100%" , borderRadius: "5px", padding: "5px", border: "1px solid #ccc", fontFamily: "Poppins"}}
                    />
                    <button
                      onClick={() => {
                        const newEmails = emails.filter((_, i) => i !== index);
                        setEmails(newEmails);
                      }}
                      className="custom-button"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setEmails([...emails, ""])}
                  className="custom-button"
                  style={{ marginBottom: "20px" }}
                >
                  Add Email

                </button>
            </div>
            <button
                    type="submit"
                    className={`submitButton ${isLoading('exportData') ? 'loading' : ''}`}
                    onClick={handleExportData}
                  >
                    {isLoading('exportData') ? (
                      <LoadingSpinner
                        size="xs"
                        inline={true}
                        message="Exporting data..."
                      />
                    ) : (
                      "Export"
                    )}
                  </button>
            </div>
          </Modal>
        )
      }
      {isModalOpen && showFile && (
        <div className="file-container">
          <Modal isOpen={isModalOpen} onClose={handleModalClose}>
            {loadingStates.fileView ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '500px',
                width: '100%'
              }}>
                <LoadingSpinner
                  overlay={false}
                  message={loadingMessages.fileView}
                  progress={loadingProgress.fileView}
                  showProgress={true}
                />
              </div>
            ) : fileUrl ? (
              <div style={{ width: "100%", height: "500px" }}>
                <FileViewer
                  fileUrl={fileUrl}
                  fileType={fileType}
                  fileName={fileUrl ? fileUrl.split('/').pop() : 'fuel-receipt'}
                />
              </div>
            ) : (
              <p>Loading file...</p>
            )}
          </Modal>
        </div>
      )}
      {showApproveConfirm && (
        <Modal isOpen={showApproveConfirm} onClose={() => setShowApproveConfirm(false)} title="Confirm Approval">
          <div style={{ padding: "1rem" }}>
            <p>{approveMessage}</p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
              <button
                className="custom-button"
                onClick={async () => {
                  setShowApproveConfirm(false);
                  await withLoading('approve', async (updateProgress) => {
                    await ApiManager.markFuelTransactionApproved(
                      new URLSearchParams({ id: currentRowId, check: 0 })
                    );
                    setFuelReceiptStatus("approved");
                    fetchData();
                    updateProgress(100);
                  }, 'Approving transaction...', 30000);
                }}
              >
                Approve Anyways
              </button>
              <button
                className="custom-button"
                onClick={() => setShowApproveConfirm(false)}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Fuels;